import { createSelector } from '@reduxjs/toolkit';

import { Uuid as EdsUuid } from '~/eds/src/types/Uuid';
import { SliceType } from '~/enums/SliceType';
import {
  getFieldByQuestionId,
  getQuestionById,
  getSectionById,
} from '~/reducers/intakeForm';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { buildQuery } from '../api/methods/search';
import {
  FormQuestion,
  FormSection,
  IntakeForm,
  QuestionField,
} from '../api/methods/ticket';
import { isActiveFilter, name, SearchSlice } from '../slices/search';
import { RootState } from '../types';
import { DocumentEditorType, getTabEditingKey } from '../types/documentEditors';

// --- ticket selectors ---

const selectTicketSlice = (rootState: any): any => rootState['ticket'];

export const selectTicketState = createSelector(
  selectTicketSlice,
  (state) => state,
);

export const selectTicketId = createSelector(
  selectTicketSlice,
  (state) => state.id,
);

export const selectTicketWorkflowId = createSelector(
  selectTicketSlice,
  (state) => state.workflowId,
);

export const selectTicketPermissions = createSelector(
  selectTicketSlice,
  (state) => state.permissions,
);

// --- documentEditors selectors ---

const selectDocumentEditorsSlice = (rootState: RootState) =>
  rootState[SliceType.DocumentEditors];

export const selectEditors = ({
  documentEditor,
  versionId,
}: {
  documentEditor: DocumentEditorType;
  versionId: Uuid;
}) =>
  createSelector(selectDocumentEditorsSlice, (state): Array<Uuid> => {
    return state.versions[versionId]?.[documentEditor] ?? [];
  });

export const selectIsCurrentTabEditing = ({
  documentEditor,
  versionId,
}: {
  documentEditor: DocumentEditorType;
  versionId: Uuid;
}) =>
  createSelector(selectDocumentEditorsSlice, (state): boolean => {
    return (
      state.tabEditing[getTabEditingKey(versionId, documentEditor)] ?? false
    );
  });

// --- search selectors ---

const selectSearchSlice = (rootState: any): SearchSlice => rootState[name];

export const selectBooleanQuery = createSelector(
  selectSearchSlice,
  (state) => state.booleanQuery,
);

export const selectModal = createSelector(
  selectSearchSlice,
  (state) => state.modal,
);

export const selectPage = createSelector(
  selectSearchSlice,
  (state) => state.page,
);

export const selectPageSize = createSelector(
  selectSearchSlice,
  (state) => state.pageSize,
);

export const selectSortBy = createSelector(
  selectSearchSlice,
  (state) => state.sortBy,
);

export const selectFieldValues = createSelector(
  selectSearchSlice,
  (state) => state.fieldValues,
);

export const selectClauseSuggestions = createSelector(
  selectSearchSlice,
  (state) => state.clause,
);

export const selectFilters = createSelector(
  selectSearchSlice,
  (state) => state.filters,
);

export const selectActiveFilters = createSelector(selectFilters, (filters) =>
  filters.filter(isActiveFilter),
);

export const selectFiltersAreDirty = createSelector(
  selectSearchSlice,
  (state) => state.filtersAreDirty,
);

export const selectFolderTree = createSelector(
  selectSearchSlice,
  (state) => state.folderTree,
);

const _selectColumnOrder = createSelector(
  selectSearchSlice,
  (state) => state.columnOrder,
);

export const selectColumnOrder = createSelector(
  _selectColumnOrder,
  (columnOrder) => columnOrder.map(String),
);

export const selectColumnWidths = createSelector(
  selectSearchSlice,
  (state) => state.columnWidths,
);

export const selectQuery = createSelector(
  selectSearchSlice,
  (state) => state.query,
);

export const selectQueryFields = createSelector(
  selectSearchSlice,
  (state) => state.queryFields,
);

export const selectUnsupportedSavedSearchQuery = createSelector(
  selectSearchSlice,
  (state) => state.unsupportedSavedSearchQuery,
);

export const selectPilotSearchQuery = createSelector(
  selectSearchSlice,
  (state) => {
    if (state.query && !state.unsupportedSavedSearchQuery) {
      return buildQuery(state.query);
    } else if (!state.query && state.unsupportedSavedSearchQuery) {
      return state.unsupportedSavedSearchQuery;
    } else if (state.query && state.unsupportedSavedSearchQuery) {
      return buildQuery(state.query);
    } else {
      return [];
    }
  },
);

export const selectCurrentTableView = createSelector(
  selectSearchSlice,
  (state) => state.currentTableView,
);

export const selectCurrentAddToGroupStep = createSelector(
  selectSearchSlice,
  (state) => state.currentAddToGroupStep,
);

export const selectSelectedDocument = createSelector(
  selectSearchSlice,
  (state) => state.selectedDocument,
);

export const selectSelectedGroup = createSelector(
  selectSearchSlice,
  (state) => state.selectedGroup,
);

export const selectSearchResultsCache = createSelector(
  selectSearchSlice,
  (state) => state.searchResultsCache,
);

export const selectPinnedFieldIds = createSelector(
  selectSearchSlice,
  (state) => state.pinnedFieldIds,
);

// --- intakeForm selectors ---

export interface QuestionFieldState extends QuestionField {
  question: string;
  errorMessage: Nullable<string>;
  modified: boolean;
  isPlaceHolder: boolean;
  isEnabled: boolean;
  warningMessage: Nullable<string>;
}

export interface FormQuestionState extends Omit<
  FormQuestion,
  'field' | 'fieldId' | 'name'
> {
  field: EdsUuid;
}

export type FormQuestionFieldState = FormQuestionState & {
  field: QuestionFieldState;
};

export interface FormSectionState extends Omit<FormSection, 'questions'> {
  questions: EdsUuid[];
}

interface IntakeFormState extends IntakeForm {
  isValidated: boolean;
  hasErrors: boolean;
  fields: QuestionFieldState[];
  questions: FormQuestionState[];
}

const selectIntakeFormSlice = (rootState: any): IntakeFormState =>
  rootState['intakeForm'];

const selectIntakeFormTicketSlice = (rootState: any): any =>
  rootState['ticket'];

export const selectIntakeForm = createSelector(
  selectIntakeFormSlice,
  (state) => state,
);

export const selectFormValidation = createSelector(
  selectIntakeFormSlice,
  (state) => state.isValidated,
);

export const selectFormHasErrors = createSelector(
  selectIntakeFormSlice,
  (state) => state.hasErrors,
);

export const selectTicket = createSelector(
  selectIntakeFormTicketSlice,
  (ticket) => ticket,
);

export const selectTicketStage = createSelector(
  selectIntakeFormTicketSlice,
  (state) => state.stage,
);

export const selectFormSection = createSelector(
  [
    selectIntakeFormSlice,
    (_intakeForm: IntakeForm, sectionId: EdsUuid) => sectionId,
  ],
  (intakeForm, sectionId): FormSectionState =>
    getSectionById(intakeForm, sectionId),
);

export const selectFormQuestion = createSelector(
  [
    selectIntakeFormSlice,
    (_intakeForm: IntakeForm, questionId: EdsUuid) => questionId,
  ],
  (intakeForm, questionId): FormQuestionState =>
    getQuestionById(intakeForm, questionId),
);

export const selectQuestionField = createSelector(
  [
    selectIntakeFormSlice,
    (_intakeForm: IntakeForm, questionId: EdsUuid) => questionId,
  ],
  (intakeForm, questionId): QuestionFieldState =>
    getFieldByQuestionId(intakeForm, questionId),
);

export const selectQuestionAndField = createSelector(
  [
    selectIntakeFormSlice,
    (_intakeForm: IntakeForm, questionId: EdsUuid) => questionId,
  ],
  (intakeForm, questionId): FormQuestionFieldState => ({
    ...getQuestionById(intakeForm, questionId),
    field: getFieldByQuestionId(intakeForm, questionId),
  }),
);

export const selectQuestionsWithFields = createSelector(
  [
    selectIntakeFormSlice,
    (_intakeForm: IntakeForm, questionIds: EdsUuid[]) => questionIds,
  ],
  (intakeForm, questionIds): Array<FormQuestionFieldState> =>
    questionIds.map((questionId) => ({
      ...getQuestionById(intakeForm, questionId),
      field: getFieldByQuestionId(intakeForm, questionId),
    })),
);

// --- workflow selectors ---

const selectWorkflowSlice = (rootState: any) => rootState['workflow'] as any;

export const selectIsTurnTrackingEnabled = createSelector(
  selectWorkflowSlice,
  (state) => state.settings.isTurnTrackingEnabled,
);

// --- builder selectors ---

const selectBuilderSlice = (rootState: any) => rootState['builder'] as any;

export const selectShouldEditSetup = createSelector(
  selectBuilderSlice,
  (state) => state.shouldEditSetup,
);

// --- aliased exports for backward compatibility ---
export { selectEditors as selectDocumentEditors };
export { selectColumnOrder as selectSearchColumnOrder };
export { selectColumnWidths as selectSearchColumnWidths };
export { selectModal as selectSearchResultModal };
export { selectPage as selectSearchPage };
export { selectPageSize as selectSearchPageSize };
export { selectSortBy as selectSearchSortBy };
export { selectFieldValues as selectSearchFieldValues };
export { selectFilters as selectSearchFilters };
export { selectActiveFilters as selectActiveSearchFilters };
export { selectFiltersAreDirty as selectSearchFiltersAreDirty };
export { selectCurrentTableView as selectSearchCurrentTableView };
export { selectCurrentAddToGroupStep as selectSearchCurrentAddToGroupStep };
export { selectSelectedDocument as selectSearchSelectedDocument };
export { selectSelectedGroup as selectSearchSelectedGroup };
